import { mobile, tablet } from "components/templates/Breakpoints";
import { H1 as _H1 } from "components/atoms/Heading";
import React from "react";
import { Image, types } from "react-bricks/frontend";
import styled from "styled-components";
import { backgroundColorProp } from "../utils/backgroundColor";
import { Repeater } from "react-bricks/frontend";
import { HeroLeftTextProps } from "./HeroLeftText";

interface HeroLandingImageProps {
  backgroundColor?: types.IColor;
  backgroundImageIsDark?: boolean;
  leftText?: types.RepeaterItems<HeroLeftTextProps>;
  backgroundImage?: types.IImageSource;
  rightImageWidth?: number;
  rightImage?: types.IImageSource;
  rightImagePosition?: "grounded" | "centered";
}

const HeroLandingImage: types.Brick<HeroLandingImageProps> = ({
  backgroundColor,
  backgroundImage,
  rightImageWidth,
  backgroundImageIsDark,
  rightImagePosition = "grounded",
}) => {
  return (
    <Container backgroundColor={backgroundColor?.color}>
      <Image
        source={backgroundImage}
        readonly={true}
        alt="Background image"
        imageStyle={{
          objectFit: "cover",
          width: "100%",
          height: "100%",
          position: "absolute",
          top: 0,
          left: 0,
          zIndex: 1,
        }}
      />
      <Left>
        <Repeater propName="leftText" itemProps={{ inverted: backgroundImageIsDark }} />
      </Left>
      <Right position={rightImagePosition}>
        <RightImageWrapper maxWidth={rightImageWidth} position={rightImagePosition}>
          <Image
            propName="rightImage"
            alt="Right image"
            maxWidth={rightImageWidth}
            imageStyle={{ width: rightImageWidth, height: "auto" }}
            placeholder={() => "/static/bricks/examples/hero_banner_person.png"}
          />
        </RightImageWrapper>
      </Right>
    </Container>
  );
};

HeroLandingImage.schema = {
  name: "hero-landing-image",
  label: "Hero Landing (Image)",
  //   previewImageUrl: "/static/bricks/hero-form.png",
  repeaterItems: [
    {
      name: "leftText",
      itemType: "hero-left-text",
      itemLabel: "Copy",
      min: 1,
      max: 1,
    },
  ],

  // Defaults when a new brick is added
  getDefaultProps: () => ({
    leftText: [
      {
        title: "Title to describe what the webinar is about to go here here here what",
        body: "Supporting copy here",
        topImageWidth: 170,
        rightImageWidth: 170,
        showBody: true,
        showBottomImage: true,
        showTopImage: true,
      },
    ],
    rightImageWidth: 640,
    rightImagePosition: "grounded",
  }),

  // Sidebar Edit controls for props
  sideEditProps: [
    backgroundColorProp,
    {
      name: "backgroundImage",
      label: "Background Image",
      type: types.SideEditPropType.Image,
    },
    {
      name: "backgroundImageIsDark",
      label: "Dark background image?",
      show: (props) => !!props.backgroundImage,
      shouldRefreshStyles: true,
      type: types.SideEditPropType.Boolean,
    },
    {
      name: "rightImageWidth",
      label: "Right Image Width",
      type: types.SideEditPropType.Number,
      shouldRefreshStyles: true,
    },
    {
      name: "rightImagePosition",
      label: "Right Image Position",
      type: types.SideEditPropType.Select,
      shouldRefreshStyles: true,
      selectOptions: {
        display: types.OptionsDisplay.Select,
        options: [
          { label: "Centered", value: "centered" },
          { label: "Bottom", value: "grounded" },
        ],
      },
    },
  ],
};

export default HeroLandingImage;

const Container = styled.div<{ backgroundColor: string }>`
  background-color: ${(p) => p.backgroundColor};
  max-width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: hidden;
  padding-left: 4rem;
  padding-right: 4rem;

  ${tablet} {
    flex-direction: column;
    padding: 4rem;
    padding-bottom: 0;
  }

  ${mobile} {
    padding: 2rem;
    padding-top: 1.5rem;
    button {
      width: 100%;
    }
  }
`;

const Left = styled.div`
  width: 55%;
  max-width: 40rem;
  z-index: 2;
  padding-top: 9rem;
  padding-bottom: 9rem;

  ${tablet} {
    width: 100%;
    padding-top: 0;
    padding-bottom: 0;
    margin-right: 0rem;
    margin-top: 5rem;
    img {
      max-width: 100%;
    }
  }

  ${mobile} {
    margin-top: 3rem;
  }
`;

const Right = styled.div<{ position: "grounded" | "centered" }>`
  width: 45%;
  max-width: 40rem;
  z-index: 2;
  height: 100vh;
  ${(p) =>
    p.position === "centered" &&
    `
    display: flex;
    align-items: center;
  `}

  ${tablet} {
    height: auto;
    width: 100%;
    padding-top: 6rem;
  }
`;

const RightImageWrapper = styled.div<{ maxWidth: number; position: "grounded" | "centered" }>`
  z-index: 2;
  ${(p) =>
    p.position === "grounded" &&
    `
    position: absolute;
    bottom: 0;
    `}

  ${tablet} {
    position: relative;
    bottom: inherit;
    img {
      max-width: 100%;
      height: auto;
    }
  }

  img {
    width: ${(p) => p.maxWidth}px;
    margin-bottom: -10px;
  }
`;
